'use client'

import type { QueryClient } from '@tanstack/react-query'

import { clearImpersonationCookie } from '@/app/actions/impersonation'
import { clearSelectedTeamCookie } from '@/app/actions/team'
import { SELECTED_TEAM_COOKIE_NAME } from '@/features/team-selection/constants'
import { clearAmplifyBrowserAuthArtifacts } from '@/services/auth/amplifyStorageCleanup.client'
import { clearQueryClientAuthStateBestEffort } from '@/services/auth/queryClientCleanup.client'
import { clearClientAuthStateMarkers } from '@/services/auth/clientAuthState'
import { clearLastActivity } from '@/services/auth/inactivity.client'
import {
  clearPersistSessionPreference,
  signOutLocalSessionBestEffort,
} from '@/services/auth/session.client'
import { emitCookieChange } from '@/utils/cookies/clientCookies'

type PerformLocalSessionCleanupOptions = {
  skipSignOut?: boolean
  clearPersistPreference?: boolean
  clearAppCookies?: boolean
  clearLegacyAuthState?: boolean
}

export async function performLocalSessionCleanup(
  queryClient: QueryClient,
  options?: PerformLocalSessionCleanupOptions,
): Promise<boolean> {
  clearLastActivity()

  if (!options?.skipSignOut) {
    await signOutLocalSessionBestEffort()
  }

  const amplifyAuthArtifactsCleared = clearAmplifyBrowserAuthArtifacts()

  await clearQueryClientAuthStateBestEffort(queryClient)

  if (options?.clearAppCookies) {
    await Promise.allSettled([
      clearImpersonationCookie(),
      clearSelectedTeamCookie(),
    ])
    emitCookieChange(SELECTED_TEAM_COOKIE_NAME)
  }

  if (options?.clearPersistPreference) {
    clearPersistSessionPreference()
  }

  if (options?.clearLegacyAuthState) {
    clearClientAuthStateMarkers()
  }

  return amplifyAuthArtifactsCleared
}
